import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  kiAnweisungSetzen,
  kiAuswertung,
  kiNutzung,
  kiEinstellungAendern,
  kiFunktionSetzen,
  kiMandantSetzen,
  kiModelle,
  kiRolleSetzen,
  kiUebersicht,
  kiVerbindungstest,
  type KiAuswertung,
  type KiBudget,
  type KiNutzung,
  type KiEinstellungsaenderung,
  type KiFunktion,
  type KiModell,
  type KiUebersicht,
  type KiVerbindungstest,
} from '../api/ki';
import { ApiFehler } from '../api/fehler';
import { kiPostfaecher, kiPostfachSetzen, type PostfachKi } from '../api/support';
import { AbTablet } from '../komponenten/AbTablet';
import { markenklasse } from '../marken';
import { Feldraster } from '../komponenten/Akte';
import { Diagramm } from '../komponenten/Diagramm';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Skelett } from '../start/Widget';
import {
  LISTENPREISE_QUELLE,
  aufwandFuer,
  fehltText,
  listenpreis,
  modelloptionen,
  stufenFuer,
  type Modellliste,
} from './modellwahl';

const dollar = (betrag: number): string =>
  `${betrag.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} US-$`;
const tausender = (zahl: number): string => zahl.toLocaleString('de-DE');
const prozent = (anteil: number | null): string =>
  anteil === null ? '—' : `${(anteil * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;

/** Auftrag CRM-47: Maßgeblich für die Rechnung ist die Konsole des Anbieters, nicht diese Zahl. */
export const ANBIETERKONSOLE = 'https://platform.claude.com/';

const KURZ = new Intl.NumberFormat('de-DE', { notation: 'compact', maximumFractionDigits: 1 });
const MONAT = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const MONAT_KURZ = new Intl.DateTimeFormat('de-DE', { month: 'short', year: '2-digit', timeZone: 'UTC' });
const monatAus = (monat: string, form: Intl.DateTimeFormat): string => form.format(new Date(`${monat}-01T00:00:00Z`));

const ERGEBNIS: Record<KiVerbindungstest['ergebnis'], string> = {
  antwort: 'Verbunden — das Modell hat geantwortet.',
  abgelehnt: 'Verbunden — das Modell hat die Testfrage abgelehnt. Schlüssel und Modell gelten.',
  unbrauchbar: 'Verbunden — die Antwort war unvollständig. Schlüssel und Modell gelten.',
};

/**
 * Die KI unter Umsysteme (KI-05 bis KI-08, KI-21).
 *
 * Die Maske beantwortet zuerst: **Läuft die KI, und wenn nicht, warum?**
 * (KI-07). Ohne Schlüssel oder ausgeschaltet bleibt alles wie bisher; die
 * Zeile oben sagt, woran es liegt und wer es beheben kann.
 *
 * DER SCHLÜSSEL STEHT HIER NICHT. Die Maske zeigt, ob er gesetzt ist und wie
 * die Variable heißt. Eintragen kann ihn nur der Betrieb, in der
 * Betriebskonfiguration der Instanz.
 *
 * EINSCHALTEN VERLANGT DIE BESTÄTIGUNG DES AUFTRAGSVERARBEITUNGSVERTRAGS
 * (KI-05). Der Server prüft das; die Maske sagt es vorher.
 *
 * DAS RASTER DER EINSTELLUNGEN (Auftrag CRM-64): „Zustand" und „Schalter"
 * sind etwa gleich hoch und teilen sich eine Reihe; der Modellzugang trägt
 * acht Felder und nimmt die ganze Breite mit Feldraster. Neben dem kurzen
 * „Zustand" stand er vorher halb breit, und darunter klaffte eine Lücke.
 */
export function Ki({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [stand, setzeStand] = useState<KiUebersicht | undefined>(undefined);
  const [entwurf, setzeEntwurf] = useState<KiEinstellungsaenderung | undefined>(undefined);
  const [auswertung, setzeAuswertung] = useState<KiAuswertung | undefined>(undefined);
  const [test, setzeTest] = useState<KiVerbindungstest | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [liste, setzeListe] = useState<Modellliste | undefined>(undefined);
  const [aufwandhinweis, setzeAufwandhinweis] = useState('');
  const [preishinweis, setzePreishinweis] = useState('');
  const [nutzung, setzeNutzung] = useState<KiNutzung | 'fehler' | undefined>(undefined);

  /* Auftrag CRM-47: Nutzung und Budget kommen für sich — die Maske wartet nicht darauf. */
  const nutzungLaden = useCallback((): void => {
    void kiNutzung()
      .then(setzeNutzung)
      .catch(() => setzeNutzung('fehler'));
  }, []);
  useEffect(() => nutzungLaden(), [nutzungLaden]);

  const uebernehmen = useCallback((neu: KiUebersicht): void => {
    setzeStand(neu);
    setzeAufwandhinweis('');
    setzePreishinweis('');
    const e = neu.einstellung;
    setzeEntwurf({
      aktiv: e.aktiv,
      modell: e.modell,
      aufwand: e.aufwand,
      hoechstbetragMonat: e.hoechstbetragMonat,
      preisEingabe: e.preisEingabe,
      preisAusgabe: e.preisAusgabe,
      avBestaetigt: e.avBestaetigtAm !== null,
      avVermerk: e.avVermerk,
    });
  }, []);

  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; „Erneut versuchen" laedt nur diese Seite. */
  const geladen = useLadestufe((signal) => Promise.all([kiUebersicht(signal), kiAuswertung(undefined, signal)]));
  const ladefehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;
  useEffect(() => {
    if (geladen.daten === undefined) return;
    const [u, a] = geladen.daten;
    uebernehmen(u);
    setzeAuswertung(a);
  }, [geladen.daten, uebernehmen]);

  // KI-21: Die Liste kommt für sich — die Maske wartet nicht auf den Anbieter.
  useEffect(() => {
    void kiModelle()
      .then(setzeListe)
      .catch(() => setzeListe({ modelle: [], grund: 'Die Abfrage beim Server ist gescheitert.' }));
  }, []);

  /** Schreibt und übernimmt den Stand; ein Fehler fliegt weiter — die Rückfrage zeigt ihn selbst (CRM-104). */
  async function schreibe(was: () => Promise<KiUebersicht | void>, meldung: string): Promise<void> {
    setzeLaeuft(true);
    setzeRueckmeldung('');
    try {
      const neu = await was();
      if (neu !== undefined) uebernehmen(neu);
      // Ein neuer Höchstbetrag oder ein Testaufruf ändert das Budget.
      nutzungLaden();
      setzeFehler(undefined);
      setzeRueckmeldung(meldung);
    } finally {
      setzeLaeuft(false);
    }
  }

  async function handle(was: () => Promise<KiUebersicht | void>, meldung: string): Promise<void> {
    try {
      await schreibe(was, meldung);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  if (stand === undefined || entwurf === undefined) {
    const anzeigefehler = fehler ?? ladefehler;
    const skelett = (
      <section className="karte">
        <Skelett art="liste" />
      </section>
    );
    return (
      <>
        {anzeigefehler !== undefined && <Meldung fehler={anzeigefehler} />}
        {/* Bis die Antwort uebernommen ist, steht weiter das Skelett. */}
        <Ladeanzeige zustand={geladen} was="Die KI-Einstellung" skelett={skelett}>
          {skelett}
        </Ladeanzeige>
      </>
    );
  }

  const gesperrt = !darfPflegen || laeuft;
  const einschaltbar = stand.eingerichtet && entwurf.avBestaetigt;
  const gewaehlt = liste?.modelle.find((m) => m.kennung === entwurf.modell);
  const stufen = stufenFuer(gewaehlt);

  /**
   * KI-21: Der Aufwand folgt dem Modell, die Preise werden vorgeschlagen,
   * wo sie bekannt sind (KI-08). Gespeichert wird erst mit „Speichern".
   */
  function modellWaehlen(kennung: string): void {
    if (entwurf === undefined) return;
    const modell = liste?.modelle.find((m) => m.kennung === kennung);
    const aufwand = aufwandFuer(stufenFuer(modell), entwurf.aufwand);
    const preis = modell === undefined ? undefined : listenpreis(modell.name);
    setzeEntwurf({
      ...entwurf,
      modell: kennung,
      aufwand,
      ...(preis === undefined ? {} : { preisEingabe: preis.eingabe, preisAusgabe: preis.ausgabe }),
    });
    setzeAufwandhinweis(
      aufwand === entwurf.aufwand ? '' : `„${entwurf.aufwand}" kann dieses Modell nicht; eingestellt ist jetzt „${aufwand}".`,
    );
    const name = modell?.name ?? kennung;
    setzePreishinweis(
      preis === undefined
        ? `Für ${name} ist kein Listenpreis hinterlegt — tragen Sie die Preise laut Preisliste des Anbieters ein.`
        : `Listenpreis für ${name} vorgeschlagen: ${dollar(preis.eingabe)} Eingabe, ${dollar(preis.ausgabe)} Ausgabe `
          + `je Million Token (${LISTENPREISE_QUELLE}). Bitte vor dem Speichern prüfen.`,
    );
  }

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      <Einstellungsseite>
        {/* ── Auftrag CRM-47: Nutzung und Budget — zuerst, auch solange die KI aus ist ── */}
        <Thema
          titel="Nutzung und Budget"
          breit
          zusatz={typeof nutzung === 'object' ? monatAus(nutzung.monat, MONAT) : undefined}
        >
          <NutzungUndBudget nutzung={nutzung} />
        </Thema>

        {/* ── KI-07: Läuft sie, und wenn nicht, warum? ───────────────────── */}
        <Thema
          titel="Zustand"
          zusatz={
            <span className={markenklasse(stand.zustand.bereit ? 'abschluss' : 'aufmerksamkeit')}>
              {stand.zustand.bereit ? 'bereit' : 'aus'}
            </span>
          }
        >
          <p className={stand.zustand.bereit ? 'hinweiskarte' : 'hinweiskarte hinweiskarte--warnung'}>
            {stand.zustand.hinweis}
            {!stand.zustand.bereit && ' Ohne KI arbeitet das System wie bisher; keine Funktion setzt sie voraus.'}
          </p>
          <p className="hilfe">
            Anbieter: {stand.anbieter}. Zugangsschlüssel ({stand.erwarteteVariablen.join(', ')}):{' '}
            <strong>{stand.eingerichtet ? 'gesetzt' : 'fehlt'}</strong> — er steht nur in der
            Betriebskonfiguration und erscheint nirgends.
          </p>
          <p className="hilfe">
            Verbraucht in diesem Monat: <strong>{dollar(stand.kostenMonat)}</strong> von{' '}
            {dollar(stand.einstellung.hoechstbetragMonat)}. Ist der Höchstbetrag erreicht, ruht die KI bis
            zum Monatswechsel.
          </p>
        </Thema>

        {/* ── KI-06: je Mandant, je Rolle, je Funktion ───────────────────── */}
        <Thema titel="Schalter">
          <Feldraster>
            <fieldset className="feld wahlgruppe">
              <legend className="feld__beschriftung">Je Mandant</legend>
              {stand.mandanten.map((m) => (
                <div key={m.mandant} className="feld feld--kasten">
                  <input
                    id={`ki-mandant-${m.mandant}`}
                    type="checkbox"
                    checked={m.aktiv}
                    disabled={gesperrt}
                    onChange={(e) => void handle(() => kiMandantSetzen(m.mandant, e.target.checked), 'Gespeichert.')}
                  />
                  <label htmlFor={`ki-mandant-${m.mandant}`}>{m.bezeichnung}</label>
                </div>
              ))}
            </fieldset>
            <fieldset className="feld wahlgruppe">
              <legend className="feld__beschriftung">Je Rolle</legend>
              {stand.rollen.map((r) => (
                <div key={r.rolle} className="feld feld--kasten">
                  <input
                    id={`ki-rolle-${r.rolle}`}
                    type="checkbox"
                    checked={r.aktiv}
                    disabled={gesperrt}
                    onChange={(e) => void handle(() => kiRolleSetzen(r.rolle, e.target.checked), 'Gespeichert.')}
                  />
                  <label htmlFor={`ki-rolle-${r.rolle}`}>{r.bezeichnung}</label>
                </div>
              ))}
              <p className="feld__hilfe">Wie bei den Rechten genügt eine zugelassene Rolle des Benutzers.</p>
            </fieldset>
          </Feldraster>
        </Thema>

        {/* ── KI-05, KI-06, KI-21: der Modellzugang ──────────────────────── */}
        <Thema titel="Modellzugang" breit>
          <Feldraster>
            <div className="feld feld--kasten">
              <input
                id="ki-av"
                type="checkbox"
                checked={entwurf.avBestaetigt}
                disabled={gesperrt}
                onChange={(e) =>
                  setzeEntwurf({ ...entwurf, avBestaetigt: e.target.checked, aktiv: e.target.checked && entwurf.aktiv })
                }
              />
              <label htmlFor="ki-av">Ein Auftragsverarbeitungsvertrag mit dem Anbieter besteht</label>
              {stand.einstellung.avBestaetigtAm !== null && (
                <p className="feld__hilfe">
                  Bestätigt am {new Date(stand.einstellung.avBestaetigtAm).toLocaleString('de-DE')}.
                </p>
              )}
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ki-av-vermerk">
                Vermerk zum Vertrag
              </label>
              <input
                id="ki-av-vermerk"
                type="text"
                placeholder="Datum und Ablage, etwa „DPA vom 01.10.2026, Vertragsordner 4“"
                value={entwurf.avVermerk ?? ''}
                disabled={gesperrt || !entwurf.avBestaetigt}
                onChange={(e) => setzeEntwurf({ ...entwurf, avVermerk: e.target.value === '' ? null : e.target.value })}
              />
            </div>

            <div className="feld feld--kasten">
              <input
                id="ki-aktiv"
                type="checkbox"
                checked={entwurf.aktiv}
                disabled={gesperrt || (!entwurf.aktiv && !einschaltbar)}
                onChange={(e) => setzeEntwurf({ ...entwurf, aktiv: e.target.checked })}
              />
              <label htmlFor="ki-aktiv">KI eingeschaltet (global)</label>
              {!einschaltbar && (
                <p className="feld__hilfe">
                  {stand.eingerichtet
                    ? 'Einschalten lässt sie sich erst mit bestätigtem Vertrag.'
                    : 'Einschalten lässt sie sich erst, wenn der Betrieb den Schlüssel gesetzt hat.'}
                </p>
              )}
            </div>
          </Feldraster>

          <Feldraster>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ki-modell">
                Modell
              </label>
              <select
                id="ki-modell"
                value={entwurf.modell}
                disabled={gesperrt}
                onChange={(e) => modellWaehlen(e.target.value)}
              >
                {modelloptionen(liste, stand.einstellung.modell).map((o) => (
                  <option key={o.kennung} value={o.kennung}>
                    {o.beschriftung}
                  </option>
                ))}
              </select>
              <p className="feld__hilfe">{modellhinweis(liste, gewaehlt)}</p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ki-aufwand">
                Aufwand
              </label>
              <select
                id="ki-aufwand"
                value={entwurf.aufwand}
                disabled={gesperrt}
                onChange={(e) => {
                  setzeAufwandhinweis('');
                  setzeEntwurf({ ...entwurf, aufwand: e.target.value as KiEinstellungsaenderung['aufwand'] });
                }}
              >
                {stufen.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
                {!stufen.includes(entwurf.aufwand) && (
                  <option value={entwurf.aufwand}>{entwurf.aufwand} — kann dieses Modell nicht</option>
                )}
              </select>
              {aufwandhinweis !== '' && (
                <p className="feld__hilfe" role="status">
                  {aufwandhinweis}
                </p>
              )}
            </div>
          </Feldraster>

          <Feldraster>
            {(
              [
                ['hoechstbetragMonat', 'Höchstbetrag je Monat (US-$)'],
                ['preisEingabe', 'Preis Eingabe (US-$ je Million Token)'],
                ['preisAusgabe', 'Preis Ausgabe (US-$ je Million Token)'],
              ] as const
            ).map(([feld, beschriftung]) => (
              <div className="feld" key={feld}>
                <label className="feld__beschriftung" htmlFor={`ki-${feld}`}>
                  {beschriftung}
                </label>
                <input
                  id={`ki-${feld}`}
                  type="number"
                  min={0}
                  step="0.01"
                  value={entwurf[feld]}
                  disabled={gesperrt}
                  onChange={(e) => setzeEntwurf({ ...entwurf, [feld]: Number(e.target.value) })}
                />
              </div>
            ))}
          </Feldraster>
          <p className="feld__hilfe">Die Preise laut Preisliste des Anbieters; sie gehen in die Kostenzahlen ein.</p>
          {preishinweis !== '' && (
            <p className="feld__hilfe" role="status">
              {preishinweis}
            </p>
          )}

          {darfPflegen && (
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf"
                disabled={laeuft || !stand.eingerichtet}
                onClick={() =>
                  void handle(async () => {
                    setzeTest(await kiVerbindungstest());
                  }, 'Der Verbindungstest ist durchgelaufen.')
                }
              >
                Verbindung testen
              </button>
              <button
                type="button"
                className="knopf knopf--haupt"
                disabled={laeuft}
                onClick={() => void handle(() => kiEinstellungAendern(entwurf), 'Die Einstellung ist gespeichert.')}
              >
                Speichern
              </button>
            </div>
          )}
          {test !== undefined && (
            <p className="hinweiskarte" role="status">
              {ERGEBNIS[test.ergebnis]} Modell: {test.modell}
              {test.modell !== test.angefragtesModell && ` (angefragt: ${test.angefragtesModell})`}, Dauer{' '}
              {(test.dauerMs / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s.
            </p>
          )}
        </Thema>

        <Thema titel="Funktionen" breit>
          <div className="tabellenhuelle">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Funktion</th>
                  <th scope="col">Eingeschaltet</th>
                  <th scope="col">Höchstbetrag je Monat</th>
                  <th scope="col">Verbraucht im Monat</th>
                </tr>
              </thead>
              <tbody>
                {stand.funktionen.map((f) => (
                  <tr key={f.kennung}>
                    <td>
                      {f.bezeichnung}{' '}
                      {!f.ausgeliefert && <span className={markenklasse('aufmerksamkeit')}>angekündigt</span>}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${f.bezeichnung} eingeschaltet`}
                        checked={f.aktiv}
                        disabled={gesperrt || !f.ausgeliefert}
                        onChange={(e) =>
                          void handle(() => kiFunktionSetzen(f.kennung, e.target.checked, f.hoechstbetragMonat), 'Gespeichert.')
                        }
                      />
                    </td>
                    <td>{f.hoechstbetragMonat === null ? 'nur Gesamtbetrag' : dollar(f.hoechstbetragMonat)}</td>
                    <td>{dollar(f.kostenMonat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Thema>

        <KiPostfaecher darfPflegen={darfPflegen} />

        {/* ── SUP-58: die Anweisung je Funktion, die eine annimmt ────────── */}
        {stand.funktionen.map(
          (f) =>
            f.anweisungVorgabe !== null && (
              <Anweisung
                key={`${f.kennung}-${f.anweisung ?? ''}`}
                funktion={f}
                vorgabe={f.anweisungVorgabe}
                gesperrt={gesperrt}
                darfPflegen={darfPflegen}
                speichere={(anweisung, meldung) => void handle(() => kiAnweisungSetzen(f.kennung, anweisung), meldung)}
                zuruecksetzen={() =>
                  schreibe(() => kiAnweisungSetzen(f.kennung, null), 'Die Anweisung steht wieder auf der Vorgabe.')
                }
              />
            ),
        )}

        {/* ── KI-08: Qualität und Kosten je Funktion und Monat ───────────── */}
        <Thema titel="Qualität und Kosten" breit>
          {auswertung === undefined || (auswertung.qualitaet.length === 0 && auswertung.kosten.length === 0) ? (
            <p className="hilfe">Noch keine Aufrufe.</p>
          ) : (
            <div className="tabellenhuelle">
              <table className="liste__tabelle">
                <thead>
                  <tr>
                    <th scope="col">Monat</th>
                    <th scope="col">Funktion</th>
                    <th scope="col">Unverändert übernommen</th>
                    <th scope="col">Bearbeitet übernommen</th>
                    <th scope="col">Verworfen</th>
                    <th scope="col">Offen</th>
                    <th scope="col">Aufrufe</th>
                    <th scope="col">Kosten</th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen(auswertung).map((z) => (
                    <tr key={`${z.monat}-${z.funktion}`}>
                      <td>{z.monat}</td>
                      <td>{z.funktion}</td>
                      <td>{z.q === undefined ? '—' : `${z.q.uebernommen} (${prozent(z.q.anteilUebernommen)})`}</td>
                      <td>{z.q === undefined ? '—' : `${z.q.geaendert} (${prozent(z.q.anteilGeaendert)})`}</td>
                      <td>{z.q === undefined ? '—' : `${z.q.verworfen} (${prozent(z.q.anteilVerworfen)})`}</td>
                      <td>{z.q?.offen ?? '—'}</td>
                      <td>{z.k?.aufrufe ?? 0}</td>
                      <td>{dollar(z.k?.kosten ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Thema>
      </Einstellungsseite>
    </>
  );
}

/** SUP-37: höchstens so lang — wie in sys.postfach. */
const HOECHSTE_TONALITAET = 2000;

const KUNDENKREIS: Readonly<Record<'firmenkunden' | 'privatkunden', string>> = {
  firmenkunden: 'Firmenkunden',
  privatkunden: 'Privatkunden',
};

/**
 * KI-06, SUP-35, SUP-38: die KI je Postfach.
 *
 * Neben dem globalen Schalter, den Rollen und den Funktionen ist das
 * Postfach der letzte Schalter: Ausgeschaltet liest die KI dort nichts
 * aus und entwirft nichts; der Posteingang bleibt voll bedienbar (KI-07,
 * SUP-34). Der Antwortentwurf ist im Postfach der Privatkunden mit der KI
 * an, in dem der Firmenkunden zuschaltbar (SUP-29, SUP-38). Ton, Anrede und
 * Gruß stehen je Postfach (SUP-37). Auf dem Telefon nur lesen (CRM-106).
 */
function KiPostfaecher({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [postfaecher, setzePostfaecher] = useState<readonly PostfachKi[] | undefined>(undefined);
  const [tonalitaet, setzeTonalitaet] = useState<Readonly<Record<string, string>>>({});
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');

  useEffect(() => {
    void kiPostfaecher()
      .then((liste) => {
        setzePostfaecher(liste);
        setzeTonalitaet(Object.fromEntries(liste.map((p) => [p.kennung, p.kiTonalitaet ?? ''])));
      })
      .catch((ursache: unknown) => setzeFehler(ursache instanceof ApiFehler ? ursache : undefined));
  }, []);

  async function setze(p: PostfachKi, aenderung: Partial<Pick<PostfachKi, 'kiAktiv' | 'kiAntwortentwurf' | 'kiTonalitaet'>>): Promise<void> {
    setzeLaeuft(true);
    setzeRueckmeldung('');
    try {
      const neu = await kiPostfachSetzen(p.kennung, {
        kiAktiv: aenderung.kiAktiv ?? p.kiAktiv,
        kiAntwortentwurf: aenderung.kiAntwortentwurf ?? p.kiAntwortentwurf,
        kiTonalitaet: aenderung.kiTonalitaet !== undefined ? aenderung.kiTonalitaet : p.kiTonalitaet,
      });
      setzePostfaecher((alt) => alt?.map((q) => (q.kennung === neu.kennung ? neu : q)));
      setzeTonalitaet((alt) => ({ ...alt, [neu.kennung]: neu.kiTonalitaet ?? '' }));
      setzeFehler(undefined);
      setzeRueckmeldung(`${neu.bezeichnung}: gespeichert.`);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  const gesperrt = !darfPflegen || laeuft;
  return (
    <Thema titel="Postfächer" breit>
      <AbTablet an={darfPflegen}>
        {fehler !== undefined && <Meldung fehler={fehler} />}
        {rueckmeldung !== '' && (
          <p className="feld__hilfe" role="status">
            {rueckmeldung}
          </p>
        )}
        {postfaecher === undefined ? (
          fehler === undefined && <Skelett art="liste" />
        ) : postfaecher.length === 0 ? (
          <p className="hilfe">Kein Postfach eingerichtet.</p>
        ) : (
          postfaecher.map((p) => {
            const feld = `ki-ton-${p.kennung}`;
            const ton = tonalitaet[p.kennung] ?? '';
            return (
              <fieldset key={p.kennung} className="feld">
                <legend>
                  {p.bezeichnung}
                  {p.kundenkreis !== null && ` — ${KUNDENKREIS[p.kundenkreis]}`}{' '}
                  {!p.aktiv && <span className={markenklasse('stillgelegt')}>stillgelegt</span>}
                </legend>
                <label className="liste__schalter">
                  <input
                    type="checkbox"
                    checked={p.kiAktiv}
                    disabled={gesperrt}
                    onChange={(e) => void setze(p, { kiAktiv: e.target.checked })}
                  />{' '}
                  KI liest E-Mails dieses Postfachs aus
                </label>
                <label className="liste__schalter">
                  <input
                    type="checkbox"
                    checked={p.kiAntwortentwurf}
                    disabled={gesperrt || !p.kiAktiv}
                    onChange={(e) => void setze(p, { kiAntwortentwurf: e.target.checked })}
                  />{' '}
                  Antwortentwürfe
                </label>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor={feld}>
                    Ton, Anrede und Gruß
                  </label>
                  <textarea
                    id={feld}
                    rows={3}
                    maxLength={HOECHSTE_TONALITAET}
                    value={ton}
                    disabled={gesperrt}
                    onChange={(e) => setzeTonalitaet((alt) => ({ ...alt, [p.kennung]: e.target.value }))}
                  />
                  <p className="feld__hilfe">
                    Leer gilt die Vorgabe des Kundenkreises: für Privatkunden freundlich und kurz mit „Ihr Service-Team",
                    für Firmenkunden sachlich mit „Ihr Vertriebsteam". Die festen Regeln des Entwurfs bleiben.
                  </p>
                </div>
                {darfPflegen && (
                  <div className="knopfreihe">
                    <button
                      type="button"
                      className="knopf"
                      disabled={gesperrt || ton === (p.kiTonalitaet ?? '')}
                      onClick={() => void setze(p, { kiTonalitaet: ton.trim() === '' ? null : ton.trim() })}
                    >
                      Ton speichern
                    </button>
                  </div>
                )}
              </fieldset>
            );
          })
        )}
        <p className="feld__hilfe">
          Ausgeschaltet bleibt das Postfach voll bedienbar. An das Modell gehen Betreff und Text der E-Mail, keine Anhänge.
        </p>
      </AbTablet>
    </Thema>
  );
}

/** SUP-58: höchstens so lang — wie in sys.ki_funktion. */
const HOECHSTE_ANWEISUNG = 4000;

/** Die Überschrift je Funktion; eine neue Funktion mit Anweisung trägt ihren Namen ein. */
const ANWEISUNGSTITEL: Readonly<Record<string, string>> = {
  rezensionsantwort: 'Anweisung für Rezensionsantworten',
};

/**
 * SUP-58: die Anweisung der Verwaltung an das Modell — Ton, Anrede, Signatur.
 *
 * Sie ERGÄNZT die festen Regeln der Funktion und ersetzt sie nicht; das sagt
 * die Maske, damit niemand im Feld Schutzregeln abzuschalten versucht. Ohne
 * eigene Anweisung steht die Vorgabe im Feld, und „Auf Vorgabe
 * zurücksetzen" führt dorthin zurück. Der Schlüssel des Elements wechselt
 * mit der gespeicherten Anweisung; so übernimmt das Feld nach dem Speichern
 * den Stand des Servers.
 */
function Anweisung({
  funktion,
  vorgabe,
  gesperrt,
  darfPflegen,
  speichere,
  zuruecksetzen,
}: {
  readonly funktion: KiFunktion;
  readonly vorgabe: string;
  readonly gesperrt: boolean;
  readonly darfPflegen: boolean;
  readonly speichere: (anweisung: string | null, meldung: string) => void;
  /** Wirft bei einem Fehler, damit die Rückfrage ihn zeigt. */
  readonly zuruecksetzen: () => Promise<void>;
}): JSX.Element {
  const gespeichert = funktion.anweisung ?? vorgabe;
  const [text, setzeText] = useState(gespeichert);
  const [frage, setzeFrage] = useState(false);
  const feld = `ki-anweisung-${funktion.kennung}`;
  const geaendert = text !== gespeichert;
  const istVorgabe = funktion.anweisung === null;

  return (
    <Thema titel={ANWEISUNGSTITEL[funktion.kennung] ?? `Anweisung: ${funktion.bezeichnung}`} breit>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={feld}>
          Anweisung an das Modell{' '}
          <span className={markenklasse(istVorgabe ? 'stillgelegt' : 'laufend')}>{istVorgabe ? 'Vorgabe' : 'eigene Fassung'}</span>
        </label>
        <textarea
          id={feld}
          rows={8}
          maxLength={HOECHSTE_ANWEISUNG}
          value={text}
          disabled={gesperrt}
          onChange={(e) => setzeText(e.target.value)}
        />
        <p className="feld__hilfe">
          Ton, Anrede und Schluss der Entwürfe. Die Anweisung ergänzt die festen Regeln der Funktion und hebt keine auf:
          keine Zusagen wie Rabatte oder Gutscheine, keine personenbezogenen Daten, nichts Erfundenes. Steht{' '}
          <code>{'{name}'}</code> im Text, bekommt das Modell den Namen des Verfassers zur Anrede — sonst nicht.
        </p>
      </div>
      {darfPflegen && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--eingriff"
            disabled={gesperrt || (istVorgabe && text === vorgabe)}
            onClick={() => setzeFrage(true)}
          >
            Auf Vorgabe zurücksetzen …
          </button>
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={gesperrt || !geaendert}
            onClick={() => speichere(text.trim() === '' ? null : text, 'Die Anweisung ist gespeichert.')}
          >
            Speichern
          </button>
        </div>
      )}
      {/*
        SUP-58: Anders als „Auf Vorgabe zurücksetzen" der Startseite überschreibt
        das gespeicherte Einstellung — neue Entwürfe folgen sofort der Vorgabe.
      */}
      {frage && (
        <Rueckfrage
          frage="Anweisung zurücksetzen?"
          unterzeile={funktion.bezeichnung}
          tat="Zurücksetzen"
          beiSchliessen={() => setzeFrage(false)}
          beiBestaetigen={async () => {
            await zuruecksetzen();
            setzeText(vorgabe);
            setzeFrage(false);
          }}
        >
          <p>
            {istVorgabe
              ? 'Gespeichert ist schon die Vorgabe; das Feld verliert die ungespeicherte Änderung.'
              : `Die eigene Fassung (${funktion.anweisung?.length ?? 0} Zeichen) weicht der mitgelieferten Vorgabe, und neue Entwürfe folgen ihr sofort.`}
            {!istVorgabe && geaendert ? ' Die ungespeicherte Änderung im Feld geht ebenfalls verloren.' : ''}
          </p>
        </Rueckfrage>
      )}
    </Thema>
  );
}

/** Was unter dem Dropdown „Modell" steht: woher die Wahl kommt, und was das gewählte kann (KI-21). */
/** Das Wort zur Stufe — die Farbe der Leiste allein sagte einer Vorlesehilfe nichts (NFA-14). */
function stufenwort(b: KiBudget): string {
  if (b.stufe === 'im_rahmen') return 'im Rahmen';
  if (b.stufe === 'knapp') return 'knapp';
  return b.anteil >= 1 ? 'erreicht — die KI ruht bis zum Monatswechsel' : 'fast erschöpft';
}

const prozentVon = (b: KiBudget): string => `${Math.round(b.anteil * 100).toLocaleString('de-DE')} %`;

/** Verbraucht gegen Höchstbetrag: gelb ab 80 %, rot ab 95 % (Auftrag CRM-47). */
function Budgetleiste({ budget, name, klein = false }: { readonly budget: KiBudget; readonly name: string; readonly klein?: boolean }): JSX.Element {
  return (
    <div
      className={klein ? 'budgetleiste budgetleiste--klein' : 'budgetleiste'}
      role="meter"
      aria-label={name}
      aria-valuemin={0}
      aria-valuemax={budget.hoechstbetrag}
      aria-valuenow={Math.min(budget.verbraucht, budget.hoechstbetrag)}
      aria-valuetext={`${dollar(budget.verbraucht)} von ${dollar(budget.hoechstbetrag)}, ${prozentVon(budget)} — ${stufenwort(budget)}`}
    >
      <div
        className={budget.stufe === 'im_rahmen' ? 'budgetleiste__fuellung' : `budgetleiste__fuellung budgetleiste__fuellung--${budget.stufe}`}
        style={{ width: `${String(Math.min(100, budget.anteil * 100))}%` }}
      />
    </div>
  );
}

function Stufe({ budget }: { readonly budget: KiBudget }): JSX.Element {
  return (
    <span className={budget.stufe === 'im_rahmen' ? 'budget__stufe' : `budget__stufe budget__stufe--${budget.stufe}`}>
      {prozentVon(budget)} · {stufenwort(budget)}
    </span>
  );
}

/** Die vier Tokenspalten und die Kosten — gleich in der Tabelle je Funktion und je Modell. */
function Nutzungszellen({ n }: { readonly n: KiNutzung['gesamt'] }): JSX.Element {
  return (
    <>
      <td className="zahl">{tausender(n.anfragen)}</td>
      <td className="zahl">{tausender(n.tokens.eingabe)}</td>
      <td className="zahl">{tausender(n.tokens.ausgabe)}</td>
      <td className="zahl">{tausender(n.tokens.cacheGelesen)}</td>
      <td className="zahl">{tausender(n.tokens.cacheGeschrieben)}</td>
      <td className="zahl">{dollar(n.kosten)}</td>
    </>
  );
}

const NUTZUNGSSPALTEN = ['Anfragen', 'Tokens Eingabe', 'Tokens Ausgabe', 'Cache gelesen', 'Cache geschrieben', 'Kosten'] as const;

/**
 * NUTZUNG UND BUDGET (Auftrag CRM-47): die Leiste des Monats gegen den
 * Höchstbetrag mit Rest und Hochrechnung, je Funktion dieselbe Leiste, im
 * laufenden Monat Anfragen, Tokens und Kosten je Funktion und je Modell,
 * darunter der Verlauf der letzten zwölf Monate — als Bild und als Tabelle.
 */
function NutzungUndBudget({ nutzung }: { readonly nutzung: KiNutzung | 'fehler' | undefined }): JSX.Element {
  if (nutzung === undefined) {
    return (
      <p className="hilfe" role="status">
        Die Nutzung wird geladen …
      </p>
    );
  }
  if (nutzung === 'fehler') return <p className="hilfe">Die Nutzung konnte nicht geladen werden.</p>;
  const b = nutzung.budget;
  const monat = monatAus(nutzung.monat, MONAT);
  const nieGenutzt = nutzung.verlauf.every((v) => v.anfragen === 0);
  return (
    <>
      <div className="budget">
        <p className="budget__kopf">
          <span>
            <span className="budget__zahl">{dollar(b.verbraucht)}</span> von {dollar(b.hoechstbetrag)} im {monat}
          </span>
          <Stufe budget={b} />
        </p>
        <Budgetleiste budget={b} name={`Budget ${monat}`} />
        <p className="budget__zusatz">
          Rest {dollar(b.rest)} · Hochrechnung bis Monatsende {dollar(b.hochrechnung)} (Tag {nutzung.tag} von{' '}
          {nutzung.tageImMonat}, gleichmäßig weitergerechnet)
        </p>
      </div>
      {nutzung.gesamt.anfragen === 0 && (
        <p className="hilfe" role="status">
          {nieGenutzt ? 'Noch keine Nutzung.' : `Noch keine Nutzung im ${monat}.`} Gezählt wird jede Anfrage an das
          Modell, auch eine abgelehnte.
        </p>
      )}

      <div className="tabellenhuelle">
        <table className="liste__tabelle nutzungstabelle">
          <caption className="nur-vorlesen">Nutzung je Funktion im {monat}</caption>
          <thead>
            <tr>
              <th scope="col">Funktion</th>
              <th scope="col">Budget</th>
              {NUTZUNGSSPALTEN.map((s) => (
                <th key={s} scope="col" className="zahl">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nutzung.funktionen.map((f) => (
              <tr key={f.kennung}>
                <th scope="row">{f.bezeichnung}</th>
                <td className="nutzungstabelle__leiste">
                  <Budgetleiste klein budget={f.budget} name={`Budget ${f.bezeichnung}`} />
                  <span className="budget__zusatz">
                    {dollar(f.budget.verbraucht)} von {dollar(f.budget.hoechstbetrag)}
                    {f.eigenerHoechstbetrag ? '' : ' (Gesamtbetrag)'} · <Stufe budget={f.budget} />
                  </span>
                </td>
                <Nutzungszellen n={f} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="tabellenhuelle">
        <table className="liste__tabelle">
          <caption className="nur-vorlesen">Nutzung je Modell im {monat}</caption>
          <thead>
            <tr>
              <th scope="col">Modell</th>
              {NUTZUNGSSPALTEN.map((s) => (
                <th key={s} scope="col" className="zahl">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nutzung.modelle.length === 0 ? (
              <tr>
                <td colSpan={NUTZUNGSSPALTEN.length + 1}>Noch keine Nutzung — kein Modell hat in diesem Monat geantwortet.</td>
              </tr>
            ) : (
              nutzung.modelle.map((m) => (
                <tr key={m.modell}>
                  <th scope="row">{m.modell}</th>
                  <Nutzungszellen n={m} />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!nieGenutzt && (
        <Diagramm
          art="balken"
          punkte={nutzung.verlauf.map((v) => ({
            schluessel: v.monat,
            beschriftung: monatAus(v.monat, MONAT_KURZ),
            wert: v.kosten,
            unvollstaendig: v.monat === nutzung.monat,
            wertText: dollar(v.kosten),
          }))}
          beschriftung="Kosten je Monat, die letzten zwölf Monate — die Zahlen stehen in der Tabelle darunter"
          achse={(w) => `${KURZ.format(w)} $`}
        />
      )}
      <div className="tabellenhuelle">
        <table className="liste__tabelle">
          <caption className="nur-vorlesen">Verlauf der letzten zwölf Monate</caption>
          <thead>
            <tr>
              <th scope="col">Monat</th>
              {NUTZUNGSSPALTEN.map((s) => (
                <th key={s} scope="col" className="zahl">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...nutzung.verlauf].reverse().map((v) => (
              <tr key={v.monat}>
                <th scope="row">
                  {monatAus(v.monat, MONAT)}
                  {v.monat === nutzung.monat && (
                    <>
                      {' '}
                      <span className="marke marke--warn">läuft</span>
                    </>
                  )}
                </th>
                <Nutzungszellen n={v} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="hilfe">
        Die Zahlen rechnet das CRM aus den Token-Angaben der Antworten und den eingestellten Preisen (API-Gegenwert).
        Maßgeblich für die Rechnung ist die{' '}
        <a href={ANBIETERKONSOLE} target="_blank" rel="noreferrer">
          Konsole von Anthropic
        </a>{' '}
        (Usage und Cost) — sie kennt auch Aufrufe an diesem System vorbei.
      </p>
    </>
  );
}

function modellhinweis(liste: Modellliste | undefined, modell: KiModell | undefined): string {
  if (liste === undefined) return 'Die Liste der Modelle wird geladen …';
  if (liste.grund !== null) return `Keine Liste der Modelle: ${liste.grund} Zur Wahl steht nur das eingestellte Modell.`;
  if (modell === undefined) return 'Dieses Modell nennt der Anbieter nicht (mehr). Wählen Sie eines aus der Liste.';
  if (modell.bedienbar === null) return 'Der Anbieter nennt nicht, was dieses Modell kann.';
  if (!modell.bedienbar) return `Der Modellzugang kann es nicht bedienen — es fehlt: ${fehltText(modell)}.`;
  return [
    modell.erschienenAm === null ? null : `Erschienen am ${new Date(modell.erschienenAm).toLocaleDateString('de-DE')}`,
    modell.kontextToken === null ? null : `Kontext ${tausender(modell.kontextToken)} Token`,
    modell.ausgabeToken === null ? null : `Ausgabe bis ${tausender(modell.ausgabeToken)} Token`,
  ]
    .filter((teil) => teil !== null)
    .join(' · ');
}

/** Qualität und Kosten zu einer Zeile je Monat und Funktion. */
function zeilen(a: KiAuswertung): readonly {
  readonly monat: string;
  readonly funktion: string;
  readonly q: KiAuswertung['qualitaet'][number] | undefined;
  readonly k: KiAuswertung['kosten'][number] | undefined;
}[] {
  const schluessel = new Set([...a.qualitaet, ...a.kosten].map((z) => `${z.monat}|${z.funktion}`));
  return [...schluessel]
    .sort((x, y) => (x < y ? 1 : -1))
    .map((s) => {
      const [monat = '', funktion = ''] = s.split('|');
      return {
        monat,
        funktion,
        q: a.qualitaet.find((z) => z.monat === monat && z.funktion === funktion),
        k: a.kosten.find((z) => z.monat === monat && z.funktion === funktion),
      };
    });
}
